import { collection, doc, type CollectionReference, type DocumentReference, type Firestore } from 'firebase/firestore';

/**
 * 컬렉션 경로.
 *
 * 이전에는 전부 users/{uid}/items 한 곳에 들어갔고, 잔고·대출은 title 을 '::balance::' 로
 * 표시하는 우회를 썼다. 여기서는 성격이 다른 데이터를 각자의 컬렉션에 둔다.
 */
export const COL = {
  entries: 'entries',
  accounts: 'accounts',
  debts: 'debts',
  pins: 'pins',
  /** 생활비 예산. */
  budgets: 'budgets',
  /** 세이브 — 잔고에 있지만 쓰지 않기로 떼어 둔 돈. */
  reserves: 'reserves',
  /** 거래 카테고리. 지우지 않고 보관한다. */
  categories: 'categories',
  /** 카테고리 예산 — 기본(그 기간부터) · 기간 하나. 결산용이고 한도에 들어가지 않는다. */
  categoryBudgets: 'categoryBudgets',
  /** 정산 기간 메모. 문서 id = 기간 열쇠(YYYY-MM). */
  ledgerNotes: 'ledgerNotes',
  /** 연간 카테고리 예산. 문서 id = 카테고리id~연도. 소비 계획이고 한도에 들어가지 않는다. */
  annualBudgets: 'annualBudgets',
  cards: 'cards',
  /** 이관 전 구조. 읽기 전용으로만 접근한다. */
  legacyItems: 'items',
} as const;

export function userDoc(db: Firestore, uid: string): DocumentReference {
  return doc(db, 'users', uid);
}

export function col(db: Firestore, uid: string, name: string): CollectionReference {
  return collection(db, 'users', uid, name);
}

export function docIn(db: Firestore, uid: string, name: string, id: string): DocumentReference {
  return doc(db, 'users', uid, name, id);
}

/**
 * 공유 보드 경로.
 *
 * `users/{uid}` 아래가 아니라 **최상위**다. 개인 경로 아래에 두면 상대에게 읽기를 열려면
 * 그 사용자 영역을 열어야 하고, 그러면 TODO 하나를 보여 주려고 아이디어·가계부·회복까지
 * 함께 열린다. 공유는 격리를 약화시키지 않는 자리에 둔다.
 *
 * 초대장은 보드와 또 다른 컬렉션이다 — 초대받은 사람은 아직 보드를 읽을 수 없으므로,
 * "어느 보드로 가면 되는가" 만 담긴 문서가 따로 있어야 한다.
 */
export const SHARED = {
  boards: 'sharedBoards',
  invites: 'sharedInvites',
  /** 보드 하위 컬렉션. */
  items: 'items',
  pins: 'pins',
  ddays: 'ddays',
  /** 함께 할 것 — 목록. */
  collections: 'collections',
  /**
   * 함께 할 것 — 항목.
   *
   * 목록 문서 **아래가 아니라** 보드 아래 평평하게 둔다. 중첩하면 목록마다 리스너를
   * 하나씩 붙이거나 `collectionGroup('items')` 을 써야 하는데, 후자는 이름이 같은
   * 공유 일정 컬렉션(`items`)까지 함께 걸린다.
   */
  collectionItems: 'collectionItems',
  /** 메모 — 게시판처럼 쌓이는 글. 고정메모도 이 중 하나다. */
  notes: 'notes',
} as const;

export function boardsCol(db: Firestore): CollectionReference {
  return collection(db, SHARED.boards);
}

export function boardDoc(db: Firestore, boardId: string): DocumentReference {
  return doc(db, SHARED.boards, boardId);
}

export function boardSubCol(db: Firestore, boardId: string, name: string): CollectionReference {
  return collection(db, SHARED.boards, boardId, name);
}

export function boardSubDoc(db: Firestore, boardId: string, name: string, id: string): DocumentReference {
  return doc(db, SHARED.boards, boardId, name, id);
}

export function inviteDoc(db: Firestore, code: string): DocumentReference {
  return doc(db, SHARED.invites, code);
}
